import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, Badge, Modal, Select, Button, Tag, Space, message, Popconfirm, Tooltip, Divider, InputNumber, Input, DatePicker, Upload, Table, Alert, Card, Grid } from 'antd';
import { PlusOutlined, DeleteOutlined, CloseCircleOutlined, CheckCircleOutlined, SettingOutlined, StarFilled, EditOutlined, UploadOutlined, DownloadOutlined, FileExcelOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { satisTakvimiAPI, urunlerAPI, gununMenuleriAPI, takvimEkstraMenuleriAPI, listeFiyatlariAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { paraInputProps } from '../utils/format';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import * as XLSX from 'xlsx';

dayjs.locale('tr');

// Excel hücresinden tarihi YYYY-MM-DD'ye çevir (Date / ISO / TR formatları)
const parseTarih = (tarihRaw) => {
  if (tarihRaw == null || tarihRaw === '') return '';
  if (tarihRaw instanceof Date) return dayjs(tarihRaw).format('YYYY-MM-DD');
  const trimmed = String(tarihRaw).trim();
  const trMatch = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (trMatch) {
    const [, dd, mm, yyyy] = trMatch;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const d = dayjs(trimmed);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

// Levenshtein distance — basit string benzerlik karşılaştırması
const levenshtein = (a, b) => {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
};

// Mobil için haftalık dikey kart görünümü (Calendar yerine)
function MobilHaftalikGorunum({ currentDate, setCurrentDate, getGunVerisi, getGununMenusu, getEkstraMenuler, isPazar, gunAcikMi, handleGunClick }) {
  const weekday = currentDate.day(); // 0=Pazar
  const offset = weekday === 0 ? -6 : 1 - weekday;
  const weekStart = currentDate.add(offset, 'day');
  const weekEnd = weekStart.add(6, 'day');
  const todayStr = dayjs().format('YYYY-MM-DD');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 4 }}>
        <Button icon={<LeftOutlined />} onClick={() => setCurrentDate(currentDate.subtract(7, 'day'))} size="small" />
        <strong style={{ fontSize: 13, textAlign: 'center', flex: 1 }}>
          {weekStart.format('D MMM')} – {weekEnd.format('D MMM YYYY')}
        </strong>
        <Button icon={<RightOutlined />} onClick={() => setCurrentDate(currentDate.add(7, 'day'))} size="small" />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
        <Button size="small" onClick={() => setCurrentDate(dayjs())}>Bugün</Button>
      </div>
      {Array.from({ length: 7 }).map((_, i) => {
        const date = weekStart.add(i, 'day');
        const items = getGunVerisi(date);
        const menu = getGununMenusu(date);
        const ekstraMenuler = getEkstraMenuler(date);
        const pazar = isPazar(date);
        const acik = gunAcikMi(date);
        const isToday = date.format('YYYY-MM-DD') === todayStr;
        return (
          <Card
            key={i}
            size="small"
            hoverable
            onClick={() => handleGunClick(date)}
            style={{
              marginBottom: 6,
              borderColor: isToday ? '#FF6B00' : undefined,
              borderWidth: isToday ? 2 : 1,
              background: pazar && !acik ? '#fafafa' : undefined,
            }}
            styles={{ body: { padding: 8 } }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <strong style={{ fontSize: 13, color: isToday ? '#FF6B00' : undefined }}>
                {date.format('dddd, D MMMM').replace(/(^|\s)(\p{L})/gu, (m) => m.toUpperCase())}
              </strong>
              {pazar && !acik && (
                <Tag color="default" style={{ margin: 0, fontSize: 10 }}>
                  <CloseCircleOutlined /> Kapalı
                </Tag>
              )}
            </div>
            {menu && (
              <div style={{ marginBottom: 4 }}>
                <Badge color="#FF6B00" text={
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    <StarFilled style={{ color: '#FF6B00', marginRight: 4, fontSize: 10 }} />
                    Günün Menüsü ({menu.kalemler.length})
                  </span>
                } />
              </div>
            )}
            {ekstraMenuler.map(em => (
              <Tag key={`em-${em.id}`} color="purple" style={{ fontSize: 11, marginBottom: 2 }}>
                {em.ad} ({em.kalemler.length})
              </Tag>
            ))}
            {items.length > 0 && (
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {items.map(item => (
                  <Tag
                    key={item.id ?? `sabit-${item.ilgili_id}-${item.tarih}`}
                    color={item.virtual ? 'green' : 'blue'}
                    style={{ fontSize: 11, margin: 0, borderStyle: item.virtual ? 'dashed' : 'solid' }}
                  >
                    {item.adi || `#${item.ilgili_id}`}
                  </Tag>
                ))}
              </div>
            )}
            {!menu && !ekstraMenuler.length && !items.length && acik && (
              <span style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>Boş</span>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function SatisTakvimi() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { urunTipiOptions, birimler } = useLookups();
  const porsiyonBirimId = birimler.find(b => b.ad === 'porsiyon')?.id;

  const [currentDate, setCurrentDate] = useState(dayjs());
  const [takvimData, setTakvimData] = useState({});
  const [menuData, setMenuData] = useState({}); // { 'YYYY-MM-DD': { id, tarih, aciklama, kalemler: [] } }
  const [ekstraMenuData, setEkstraMenuData] = useState({}); // { 'YYYY-MM-DD': [ {id, ad, kalemler: []}, ... ] }
  const [urunler, setUrunler] = useState([]);
  const [sabitUrunler, setSabitUrunler] = useState([]);
  const [loading, setLoading] = useState(false);

  // Gün detay modal
  const [gunModalOpen, setGunModalOpen] = useState(false);
  const [seciliGun, setSeciliGun] = useState(null);
  const [ekleUrunId, setEkleUrunId] = useState(null);

  // Menü düzenleme modal (Günün Menüsü)
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [menuForm, setMenuForm] = useState({ aciklama: '', kalemler: [] });

  // Ekstra Menü modal
  const [ekstraMenuModalOpen, setEkstraMenuModalOpen] = useState(false);
  const [ekstraMenuEditId, setEkstraMenuEditId] = useState(null);
  const [ekstraMenuForm, setEkstraMenuForm] = useState({ ad: '', aciklama: '', kalemler: [] });

  // Sabit ürün modal
  const [sabitModalOpen, setSabitModalOpen] = useState(false);
  const [sabitUrunId, setSabitUrunId] = useState(null);

  // Excel yükleme modal
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importYilAy, setImportYilAy] = useState(dayjs()); // sadece şablon üretimi için
  const [importPreview, setImportPreview] = useState(null); // { satirlar, ozet, bulunamayanUrunler }
  const [importRawRows, setImportRawRows] = useState(null); // ham satırlar (ürün eklenince yeniden validate)
  // Excel'deki tarihleri kapsayan ay(lar)ın mevcut menüleri (çakışma kontrolü için)
  const [importMevcutGunun, setImportMevcutGunun] = useState({}); // { 'YYYY-MM-DD': true }
  const [importMevcutEkstra, setImportMevcutEkstra] = useState({}); // { 'YYYY-MM-DD': [{ad}] }
  const [importLoading, setImportLoading] = useState(false);

  // Hızlı ürün ekleme modal (bulunamayan ürünler için)
  const [hizliUrunModalOpen, setHizliUrunModalOpen] = useState(false);
  const [hizliUrunForm, setHizliUrunForm] = useState({ urun_adi: '', aciklama: '', urun_tipi_id: null, satis_fiyati: null });
  // Hızlı ürün eklendikten sonra atanacak hedef: { kaynak:'gunun'|'ekstra'|'excel', idx? }
  const [hizliUrunHedef, setHizliUrunHedef] = useState(null);

  // Menü editörlerinde ürün arama metni (bulunamadı durumunda yeni ürün ekleme tetiği için)
  const [urunArama, setUrunArama] = useState('');

  const fetchTakvim = useCallback(async (date) => {
    setLoading(true);
    try {
      const res = await satisTakvimiAPI.getByMonth(date.year(), date.month() + 1);
      const grouped = {};
      res.data.forEach(item => {
        const key = item.tarih.split('T')[0];
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      });
      setTakvimData(grouped);

      const menuRes = await gununMenuleriAPI.getByMonth(date.year(), date.month() + 1);
      const menuByDate = {};
      (menuRes.data || []).forEach(m => { menuByDate[m.tarih] = m; });
      setMenuData(menuByDate);

      const ekstraRes = await takvimEkstraMenuleriAPI.getByMonth(date.year(), date.month() + 1);
      const ekstraByDate = {};
      (ekstraRes.data || []).forEach(m => {
        if (!ekstraByDate[m.tarih]) ekstraByDate[m.tarih] = [];
        ekstraByDate[m.tarih].push(m);
      });
      setEkstraMenuData(ekstraByDate);
    } catch (err) {
      message.error('Takvim verileri yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBaseData = useCallback(async () => {
    try {
      const urunRes = await urunlerAPI.getAll();
      setUrunler(urunRes.data);
    } catch (err) { console.error('Urunler yuklenemedi', err); }
    try {
      const sabitRes = await satisTakvimiAPI.getSabitUrunler();
      setSabitUrunler(sabitRes.data);
    } catch (err) { console.error('Sabit urunler yuklenemedi', err); }
  }, []);

  useEffect(() => { fetchBaseData(); }, [fetchBaseData]);
  useEffect(() => { fetchTakvim(currentDate); }, [currentDate, fetchTakvim]);

  const isPazar = (date) => date.day() === 0;

  const getGunVerisi = (date) => {
    const key = date.format('YYYY-MM-DD');
    return takvimData[key] || [];
  };

  const getGununMenusu = (date) => {
    const key = date.format('YYYY-MM-DD');
    return menuData[key] || null;
  };

  const getEkstraMenuler = (date) => {
    const key = date.format('YYYY-MM-DD');
    return ekstraMenuData[key] || [];
  };

  const gunAcikMi = (date) => {
    if (!isPazar(date)) return true;
    return getGunVerisi(date).length > 0 || !!getGununMenusu(date) || getEkstraMenuler(date).length > 0;
  };

  const handleGunClick = (date) => {
    setSeciliGun(date);
    setEkleUrunId(null);
    setGunModalOpen(true);
  };

  const handleUrunEkle = async () => {
    if (!ekleUrunId) { message.warning('Lutfen bir urun secin'); return; }
    try {
      await satisTakvimiAPI.create({
        tarih: seciliGun.format('YYYY-MM-DD'),
        takvim_tipi: 'urun',
        ilgili_id: ekleUrunId,
        sabit: 0,
      });
      message.success('Eklendi');
      setEkleUrunId(null);
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleKayitSil = async (id) => {
    try {
      await satisTakvimiAPI.remove(id);
      message.success('Silindi');
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Silinemedi');
    }
  };

  const handleMenuAc = () => {
    const mevcut = getGununMenusu(seciliGun);
    if (mevcut) {
      setMenuForm({ aciklama: mevcut.aciklama || '', kalemler: mevcut.kalemler.map(k => ({ urun_id: k.urun_id, adet: k.adet })) });
    } else {
      setMenuForm({ aciklama: '', kalemler: [] });
    }
    setMenuModalOpen(true);
  };

  const handleMenuKaydet = async () => {
    const toplamAdet = menuForm.kalemler.reduce((s, k) => s + Number(k.adet || 0), 0);
    if (menuForm.kalemler.length < 3 || toplamAdet < 3) {
      message.error('Menüde en az 3 ürün kalemi (toplam adet ≥ 3) olmalıdır.');
      return;
    }
    if (menuForm.kalemler.some(k => !k.urun_id || !k.adet || k.adet < 1)) {
      message.error('Her kalemde ürün seçili ve adet ≥ 1 olmalıdır.');
      return;
    }
    try {
      await gununMenuleriAPI.upsert({
        tarih: seciliGun.format('YYYY-MM-DD'),
        aciklama: menuForm.aciklama || null,
        kalemler: menuForm.kalemler,
      });
      message.success('Günün menüsü kaydedildi');
      setMenuModalOpen(false);
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleMenuSil = async () => {
    const mevcut = getGununMenusu(seciliGun);
    if (!mevcut) return;
    try {
      await gununMenuleriAPI.remove(mevcut.id);
      message.success('Günün menüsü silindi');
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Silinemedi');
    }
  };

  const handleEkstraMenuAc = (mevcut) => {
    if (mevcut) {
      setEkstraMenuEditId(mevcut.id);
      setEkstraMenuForm({
        ad: mevcut.ad || '',
        aciklama: mevcut.aciklama || '',
        kalemler: mevcut.kalemler.map(k => ({ urun_id: k.urun_id, adet: k.adet })),
      });
    } else {
      setEkstraMenuEditId(null);
      setEkstraMenuForm({ ad: '', aciklama: '', kalemler: [] });
    }
    setEkstraMenuModalOpen(true);
  };

  const handleEkstraMenuKaydet = async () => {
    if (!ekstraMenuForm.ad.trim()) {
      message.error('Menü adı zorunludur');
      return;
    }
    if (ekstraMenuForm.kalemler.length === 0) {
      message.error('En az bir ürün ekleyin');
      return;
    }
    if (ekstraMenuForm.kalemler.some(k => !k.urun_id || !k.adet || k.adet < 1)) {
      message.error('Her kalemde ürün seçili ve adet ≥ 1 olmalıdır');
      return;
    }
    try {
      if (ekstraMenuEditId) {
        await takvimEkstraMenuleriAPI.update(ekstraMenuEditId, {
          ad: ekstraMenuForm.ad,
          aciklama: ekstraMenuForm.aciklama || null,
          kalemler: ekstraMenuForm.kalemler,
        });
        message.success('Ekstra menü güncellendi');
      } else {
        await takvimEkstraMenuleriAPI.create({
          tarih: seciliGun.format('YYYY-MM-DD'),
          ad: ekstraMenuForm.ad,
          aciklama: ekstraMenuForm.aciklama || null,
          kalemler: ekstraMenuForm.kalemler,
        });
        message.success('Ekstra menü eklendi');
      }
      setEkstraMenuModalOpen(false);
      setEkstraMenuEditId(null);
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleEkstraMenuSil = async (id) => {
    try {
      await takvimEkstraMenuleriAPI.remove(id);
      message.success('Ekstra menü silindi');
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Silinemedi');
    }
  };

  // === Excel ile aylık menü yükleme ===

  const downloadTemplate = () => {
    const yil = importYilAy.year();
    const ay = importYilAy.month(); // 0-indexed (Date için)
    const ayStr = String(ay + 1).padStart(2, '0');
    const sonGun = new Date(yil, ay + 1, 0).getDate(); // o ayın son günü

    const rows = [['tarih', 'menu_adi', 'urun_adi', 'adet']];
    for (let g = 1; g <= sonGun; g++) {
      const d = new Date(yil, ay, g);
      if (d.getDay() === 0) continue; // Pazar atla
      for (let i = 0; i < 4; i++) {
        rows.push([d, '', '', 1]);
      }
    }

    const ws = XLSX.utils.aoa_to_sheet(rows, { cellDates: true });
    // Tarih kolonunu (A) gg.aa.yyyy biçimine al
    for (let r = 1; r < rows.length; r++) {
      const ref = XLSX.utils.encode_cell({ r, c: 0 });
      if (ws[ref]) {
        ws[ref].t = 'd';
        ws[ref].z = 'dd.mm.yyyy';
      }
    }
    ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 30 }, { wch: 6 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menüler');
    XLSX.writeFile(wb, `menu-yukleme-sablonu-${yil}-${ayStr}.xlsx`);
  };

  // Ham excel satırlarını mevcut ürün listesi + mevcut menülere göre doğrula → preview state'i kur
  const dogrulaVeOnizle = useCallback((rows, urunlerListesi, mevcutGununMap, mevcutEkstraMap) => {
    const urunByAd = new Map();
    urunlerListesi.forEach(u => urunByAd.set(u.urun_adi.toLowerCase().trim(), u));

    const mevcutGunun = new Set(Object.keys(mevcutGununMap || {}));
    const mevcutEkstra = new Set();
    Object.entries(mevcutEkstraMap || {}).forEach(([tarih, list]) => {
      list.forEach(em => mevcutEkstra.add(`${tarih}|${em.ad.toLowerCase().trim()}`));
    });

    const satirlar = [];
    rows.forEach((r, idx) => {
      const urun_adi = String(r.urun_adi || '').trim();
      if (!urun_adi) return; // boş ürün satırı: atla

      const tarih = parseTarih(r.tarih);
      const menu_adi = String(r.menu_adi || '').trim();
      const adet = Number(r.adet) || 0;
      const urun = urunByAd.get(urun_adi.toLowerCase());
      const hatalar = [];
      if (!tarih) hatalar.push('tarih geçersiz');
      if (!urun) hatalar.push('ürün bulunamadı');
      if (adet < 1) hatalar.push('adet ≥ 1 olmalı');
      satirlar.push({
        satir: idx + 2,
        tarih, menu_adi, urun_adi, adet,
        urun_id: urun ? urun.id : null,
        hatalar,
      });
    });

    // Menü grupları
    const grupMap = new Map();
    satirlar.forEach(s => {
      if (s.hatalar.length > 0) return;
      const key = `${s.tarih}|${s.menu_adi.toLowerCase()}`;
      if (!grupMap.has(key)) {
        grupMap.set(key, { tarih: s.tarih, ad: s.menu_adi, kalemler: [] });
      }
      grupMap.get(key).kalemler.push({ urun_id: s.urun_id, adet: s.adet, urun_adi: s.urun_adi });
    });
    const menuler = Array.from(grupMap.values()).map(g => {
      const isGunun = !g.ad;
      let durum = 'eklenecek';
      if (isGunun && mevcutGunun.has(g.tarih)) durum = 'atlanacak';
      else if (!isGunun && mevcutEkstra.has(`${g.tarih}|${g.ad.toLowerCase()}`)) durum = 'atlanacak';
      return { ...g, isGunun, durum };
    }).sort((a, b) => a.tarih.localeCompare(b.tarih) || (a.ad || '').localeCompare(b.ad || ''));

    const hataliSatirlar = satirlar.filter(s => s.hatalar.length > 0);

    // Bulunamayan ürünleri tekilleştir + benzer öneriler hesapla
    const bulunamayanMap = new Map();
    hataliSatirlar.filter(s => s.hatalar.includes('ürün bulunamadı')).forEach(s => {
      const key = s.urun_adi.toLowerCase();
      if (!bulunamayanMap.has(key)) {
        bulunamayanMap.set(key, { urun_adi: s.urun_adi, satirSayisi: 0, oneriler: [] });
      }
      bulunamayanMap.get(key).satirSayisi += 1;
    });
    bulunamayanMap.forEach(item => {
      const q = item.urun_adi.toLowerCase().trim();
      const scored = urunlerListesi.map(u => {
        const ad = u.urun_adi.toLowerCase().trim();
        const dist = levenshtein(ad, q);
        const maxLen = Math.max(ad.length, q.length) || 1;
        return { urun: u, oran: 1 - dist / maxLen };
      });
      item.oneriler = scored
        .filter(x => x.oran >= 0.5)
        .sort((a, b) => b.oran - a.oran)
        .slice(0, 3)
        .map(x => x.urun);
    });
    const bulunamayanUrunler = Array.from(bulunamayanMap.values());

    setImportPreview({
      menuler,
      hataliSatirlar,
      bulunamayanUrunler,
      ozet: {
        toplamSatir: rows.length,
        hataliSatir: hataliSatirlar.length,
        eklenecek: menuler.filter(m => m.durum === 'eklenecek').length,
        atlanacak: menuler.filter(m => m.durum === 'atlanacak').length,
      },
    });
  }, []);

  const parseExcelDosyasi = (file) => {
    setImportLoading(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
        setImportRawRows(rows);

        // Excel'deki tüm benzersiz ayları bul → o aylar için mevcut menüleri çek
        const aySetTarihli = new Set();
        rows.forEach(r => {
          if (!String(r.urun_adi || '').trim()) return;
          const t = parseTarih(r.tarih);
          if (t) aySetTarihli.add(t.substring(0, 7));
        });

        const gununMap = {};
        const ekstraMap = {};
        await Promise.all([...aySetTarihli].map(async (ayKey) => {
          const [y, m] = ayKey.split('-');
          try {
            const [gRes, eRes] = await Promise.all([
              gununMenuleriAPI.getByMonth(Number(y), Number(m)),
              takvimEkstraMenuleriAPI.getByMonth(Number(y), Number(m)),
            ]);
            (gRes.data || []).forEach(row => { gununMap[row.tarih] = row; });
            (eRes.data || []).forEach(row => {
              if (!ekstraMap[row.tarih]) ekstraMap[row.tarih] = [];
              ekstraMap[row.tarih].push(row);
            });
          } catch {
            // ay verisi alınamazsa o ayı boş varsayarız
          }
        }));

        setImportMevcutGunun(gununMap);
        setImportMevcutEkstra(ekstraMap);
        dogrulaVeOnizle(rows, urunler, gununMap, ekstraMap);
      } catch (err) {
        message.error('Excel okunamadı: ' + err.message);
        setImportPreview(null);
        setImportRawRows(null);
      } finally {
        setImportLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // antd Upload otomatik upload etmesin
  };

  // Hızlı ürün ekleme: yeni ürün eklendikten sonra urun listesini tazele
  // hedef: { kaynak:'gunun'|'ekstra'|'excel', idx? } — yeni ürünü doğrudan ilgili kaleme ata
  const handleHizliUrunAcUrunAdiyla = (urun_adi, hedef = { kaynak: 'excel' }) => {
    setHizliUrunForm({ urun_adi, aciklama: '', urun_tipi_id: null, satis_fiyati: null });
    setHizliUrunHedef(hedef);
    setHizliUrunModalOpen(true);
    setUrunArama('');
  };

  const handleHizliUrunKaydet = async () => {
    const { urun_adi, aciklama, urun_tipi_id, satis_fiyati } = hizliUrunForm;
    if (!urun_adi.trim()) { message.error('Ürün adı zorunludur'); return; }
    try {
      const createRes = await urunlerAPI.create({
        urun_adi: urun_adi.trim(),
        aciklama: aciklama || null,
        urun_tipi_id: urun_tipi_id || null,
        birim_id: porsiyonBirimId || null,
        satis_fiyati: satis_fiyati || null,
      });
      const yeniUrunId = createRes.data?.id;
      message.success('Ürün eklendi');
      setHizliUrunModalOpen(false);
      // Ürünleri yeniden yükle
      const res = await urunlerAPI.getAll();
      setUrunler(res.data);

      // Hedefe göre yeni ürünü ilgili kaleme ata
      if (hizliUrunHedef?.kaynak === 'gunun' && yeniUrunId != null) {
        setMenuForm(f => {
          const copy = [...f.kalemler];
          if (hizliUrunHedef.idx != null && copy[hizliUrunHedef.idx]) {
            copy[hizliUrunHedef.idx] = { ...copy[hizliUrunHedef.idx], urun_id: yeniUrunId };
          }
          return { ...f, kalemler: copy };
        });
      } else if (hizliUrunHedef?.kaynak === 'ekstra' && yeniUrunId != null) {
        setEkstraMenuForm(f => {
          const copy = [...f.kalemler];
          if (hizliUrunHedef.idx != null && copy[hizliUrunHedef.idx]) {
            copy[hizliUrunHedef.idx] = { ...copy[hizliUrunHedef.idx], urun_id: yeniUrunId };
          }
          return { ...f, kalemler: copy };
        });
      } else if (hizliUrunHedef?.kaynak === 'excel') {
        if (importRawRows) dogrulaVeOnizle(importRawRows, res.data, importMevcutGunun, importMevcutEkstra);
      }
      setHizliUrunHedef(null);
    } catch (err) {
      message.error('Ürün eklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  // Menü editöründeki Select için "bulunamadı" içeriği — yeni ürün ekleme bağlantısı
  const renderUrunNotFound = (kaynak, idx) => urunArama ? (
    <div style={{ textAlign: 'center', padding: 8 }}>
      <div style={{ color: '#999', marginBottom: 4, fontSize: 12 }}>"{urunArama}" bulunamadı</div>
      <Button type="link" size="small" icon={<PlusOutlined />}
        onClick={() => handleHizliUrunAcUrunAdiyla(urunArama, { kaynak, idx })}>
        "{urunArama}" adıyla ürün ekle
      </Button>
    </div>
  ) : null;

  // Ürün tipi seçildiğinde liste fiyatından default satış fiyatını al
  const handleHizliUrunTipiDegisti = async (val) => {
    setHizliUrunForm(f => ({ ...f, urun_tipi_id: val }));
    if (val) {
      try {
        const res = await listeFiyatlariAPI.getByTip('urun', val);
        if (res.data?.fiyat != null) {
          setHizliUrunForm(f => ({ ...f, satis_fiyati: Number(res.data.fiyat) }));
        }
      } catch {}
    }
  };

  const handleImportUygula = async () => {
    if (!importPreview) return;
    const eklenecek = importPreview.menuler.filter(m => m.durum === 'eklenecek');
    if (eklenecek.length === 0) {
      message.warning('Eklenecek menü yok');
      return;
    }
    try {
      const payload = {
        menuler: eklenecek.map(m => ({
          tarih: m.tarih,
          ad: m.ad || '',
          kalemler: m.kalemler.map(k => ({ urun_id: k.urun_id, adet: k.adet })),
        })),
      };
      const res = await satisTakvimiAPI.menuleriYukle(payload);
      const r = res.data;
      message.success(`İçe aktarıldı: ${r.eklenen_gunun} günün menüsü, ${r.eklenen_ekstra} ekstra menü${r.atlanan ? `, ${r.atlanan} atlandı` : ''}`);
      setImportModalOpen(false);
      setImportPreview(null);
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('İçe aktarım hatası: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleSabitUrunEkle = async () => {
    if (!sabitUrunId) return;
    try {
      await satisTakvimiAPI.addSabitUrun(sabitUrunId);
      message.success('Sabit urun eklendi');
      setSabitUrunId(null);
      fetchBaseData();
      fetchTakvim(currentDate);
    } catch (err) {
      message.error(err.response?.data?.hata || 'Eklenemedi');
    }
  };

  const handleSabitUrunSil = async (id) => {
    try {
      await satisTakvimiAPI.removeSabitUrun(id);
      message.success('Sabit urun kaldirildi');
      fetchBaseData();
      fetchTakvim(currentDate);
    } catch (err) {
      message.error('Silinemedi');
    }
  };

  const urunSecenekleri = urunler.map(u => ({ value: u.id, label: u.urun_adi }));
  const sabitUrunIds = sabitUrunler.map(s => s.urun_id);

  const dateCellRender = (date) => {
    if (date.month() !== currentDate.month()) return null;
    const items = getGunVerisi(date);
    const menu = getGununMenusu(date);
    const ekstraMenuler = getEkstraMenuler(date);
    const pazar = isPazar(date);
    const acik = gunAcikMi(date);

    let bgColor = 'transparent';
    if (pazar && !acik) bgColor = '#f5f5f5';
    else if (!menu && acik) bgColor = '#fff7e6';

    return (
      <div
        onClick={(e) => { e.stopPropagation(); handleGunClick(date); }}
        style={{
          minHeight: 80, padding: 4, borderRadius: 6,
          backgroundColor: bgColor, cursor: 'pointer', position: 'relative',
          border: pazar && !acik ? '1px dashed #d9d9d9' : 'none',
        }}
      >
        {pazar && !acik && (
          <div style={{ textAlign: 'center', color: '#bbb', fontSize: 11, fontStyle: 'italic' }}>
            <CloseCircleOutlined /> Kapali
          </div>
        )}
        {menu && (
          <div style={{ marginBottom: 2 }}>
            <Badge
              color="#FF6B00"
              text={<span style={{ fontSize: 11, fontWeight: 700 }}>
                <StarFilled style={{ color: '#FF6B00', marginRight: 3, fontSize: 10 }} />
                Günün Menüsü ({menu.kalemler.length})
              </span>}
            />
          </div>
        )}
        {ekstraMenuler.map(em => (
          <div key={`em-${em.id}`} style={{ marginBottom: 1 }}>
            <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', margin: 0, lineHeight: '18px' }}>
              {em.ad} ({em.kalemler.length})
            </Tag>
          </div>
        ))}
        {items.map(item => (
          <div key={item.id ?? `sabit-${item.ilgili_id}-${item.tarih}`} style={{ marginBottom: 1 }}>
            <Tag color={item.virtual ? 'green' : 'blue'}
              style={{ fontSize: 10, padding: '0 4px', margin: 0, lineHeight: '18px',
                       borderStyle: item.virtual ? 'dashed' : 'solid' }}>
              {item.adi || `Urun #${item.ilgili_id}`}
            </Tag>
          </div>
        ))}
      </div>
    );
  };

  const seciliGunItems = seciliGun ? getGunVerisi(seciliGun) : [];
  const seciliGunMenu = seciliGun ? getGununMenusu(seciliGun) : null;
  const seciliGunEkstraMenuler = seciliGun ? getEkstraMenuler(seciliGun) : [];
  const seciliPazar = seciliGun ? isPazar(seciliGun) : false;
  const seciliAcik = seciliGun ? gunAcikMi(seciliGun) : true;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Satis Takvimi</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          {!isMobile && (
            <Tooltip title="Aylık menüleri Excel ile içe aktar">
              <Button icon={<FileExcelOutlined />} onClick={() => { setImportYilAy(currentDate); setImportPreview(null); setImportRawRows(null); setImportMevcutGunun({}); setImportMevcutEkstra({}); setImportModalOpen(true); }}>
                {screens.lg && 'Excel ile Yükle'}
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Her açık güne otomatik eklenen ürünleri yönet">
            <Button icon={<SettingOutlined />} onClick={() => setSabitModalOpen(true)}>
              {!isMobile && 'Sabit Ürünler'}
            </Button>
          </Tooltip>
        </Space>
      </div>

      {!isMobile && (
        <div style={{ marginBottom: 8 }}>
          <Space size="middle" wrap>
            <span><StarFilled style={{ color: '#FF6B00', fontSize: 12 }} /> <Badge color="#FF6B00" text={<span style={{ fontSize: 12 }}>Günün Menüsü</span>} /></span>
            <span><Tag color="purple" style={{ fontSize: 10, padding: '0 4px' }}>Ekstra Menü</Tag></span>
            <span><Tag color="blue" style={{ fontSize: 10, padding: '0 4px' }}>Ürün</Tag></span>
            <span style={{ backgroundColor: '#fff7e6', padding: '2px 8px', borderRadius: 4, fontSize: 12 }}>Menü tanımlanmamış</span>
            <span style={{ backgroundColor: '#f5f5f5', padding: '2px 8px', borderRadius: 4, fontSize: 12, border: '1px dashed #d9d9d9' }}>Kapalı (Pazar)</span>
          </Space>
        </div>
      )}

      {isMobile ? (
        <MobilHaftalikGorunum
          currentDate={currentDate}
          setCurrentDate={setCurrentDate}
          getGunVerisi={getGunVerisi}
          getGununMenusu={getGununMenusu}
          getEkstraMenuler={getEkstraMenuler}
          isPazar={isPazar}
          gunAcikMi={gunAcikMi}
          handleGunClick={handleGunClick}
        />
      ) : (
        <Calendar
          value={currentDate}
          onPanelChange={(date) => setCurrentDate(date)}
          cellRender={(date, info) => info.type === 'date' ? dateCellRender(date) : null}
          loading={loading}
        />
      )}

      {/* Gün Detay Modal */}
      <Modal
        title={seciliGun ? (
          <Space>
            <span>{seciliGun.format('DD MMMM YYYY dddd')}</span>
            {seciliPazar && !seciliAcik && <Tag color="red">Kapali</Tag>}
            {seciliPazar && seciliAcik && <Tag color="green">Acik</Tag>}
          </Space>
        ) : ''}
        open={gunModalOpen}
        onCancel={() => { setGunModalOpen(false); setSeciliGun(null); setEkleUrunId(null); }}
        footer={null}
        width={600}
      >
        {seciliGun && (
          <>
            {/* Günün Menüsü */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ margin: 0 }}>Günün Menüsü</h4>
                <Space>
                  <Button size="small" type="primary" icon={seciliGunMenu ? <EditOutlined /> : <PlusOutlined />} onClick={handleMenuAc}>
                    {seciliGunMenu ? 'Düzenle' : 'Menü Tanımla'}
                  </Button>
                  {seciliGunMenu && (
                    <Popconfirm title="Günün menüsünü silmek istediğinize emin misiniz?" onConfirm={handleMenuSil} okText="Evet" cancelText="Hayır">
                      <Button danger size="small" icon={<DeleteOutlined />} />
                    </Popconfirm>
                  )}
                </Space>
              </div>
              {seciliGunMenu ? (
                <div style={{ padding: '8px 12px', background: '#FFF3E6', border: '1px solid #FF6B00', borderRadius: 6 }}>
                  {seciliGunMenu.aciklama && <div style={{ fontStyle: 'italic', color: '#666', marginBottom: 6 }}>{seciliGunMenu.aciklama}</div>}
                  {seciliGunMenu.kalemler.map(k => (
                    <div key={k.id} style={{ fontSize: 13, marginBottom: 2 }}>
                      <Tag color="orange" style={{ fontSize: 11 }}>{k.urun_tipi_adi || '-'}</Tag>
                      {k.urun_adi} <span style={{ color: '#999' }}>× {k.adet}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: 8, background: '#fff7e6', borderRadius: 4, fontSize: 12, color: '#d48806' }}>
                  Bu gün için menü tanımlanmamış. En az 3 ürün ekleyerek menü oluşturabilirsiniz.
                </div>
              )}
            </div>

            <Divider style={{ margin: '12px 0 8px' }} />

            {/* Ekstra Menüler */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h4 style={{ margin: 0 }}>Ekstra Menüler</h4>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => handleEkstraMenuAc(null)}>
                  Ekstra Menü Ekle
                </Button>
              </div>
              {seciliGunEkstraMenuler.length === 0 ? (
                <div style={{ padding: 8, background: '#fafafa', borderRadius: 4, fontSize: 12, color: '#999' }}>
                  Bu gün için ekstra menü tanımlanmamış.
                </div>
              ) : (
                seciliGunEkstraMenuler.map(em => (
                  <div key={em.id} style={{ padding: '8px 12px', background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 6, marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <strong>{em.ad}</strong>
                      <Space>
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEkstraMenuAc(em)} />
                        <Popconfirm title="Ekstra menüyü silmek istediğinize emin misiniz?" onConfirm={() => handleEkstraMenuSil(em.id)} okText="Evet" cancelText="Hayır">
                          <Button danger size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    </div>
                    {em.aciklama && <div style={{ fontStyle: 'italic', color: '#666', marginBottom: 4, fontSize: 12 }}>{em.aciklama}</div>}
                    {em.kalemler.map(k => (
                      <div key={k.id} style={{ fontSize: 13, marginBottom: 2 }}>
                        <Tag color="purple" style={{ fontSize: 11 }}>{k.urun_tipi_adi || '-'}</Tag>
                        {k.urun_adi} <span style={{ color: '#999' }}>× {k.adet}</span>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            <Divider style={{ margin: '12px 0 8px' }} />

            {/* Sabit Ürünler */}
            <h4 style={{ marginBottom: 8 }}>Ek Ürünler (tekil)</h4>
            {seciliGunItems.length > 0 && (
              <>
                {seciliGunItems.map(item => (
                  <div key={item.id ?? `sabit-${item.ilgili_id}-${item.tarih}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                    <Space>
                      {item.virtual
                        ? <Tag color="green" style={{ borderStyle: 'dashed' }}>Sabit</Tag>
                        : <Tag color="blue">Urun</Tag>}
                      <span>{item.adi || `#${item.ilgili_id}`}</span>
                    </Space>
                    {item.virtual ? (
                      <span style={{ fontSize: 11, color: '#999' }}>Her gün otomatik</span>
                    ) : (
                      <Popconfirm title="Kaldirilsin mi?" onConfirm={() => handleKayitSil(item.id)} okText="Evet" cancelText="Hayir">
                        <Button danger size="small" icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </div>
                ))}
              </>
            )}
            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
              <Select
                value={ekleUrunId} onChange={setEkleUrunId}
                style={{ flex: 1, minWidth: 200 }}
                placeholder="Urun seciniz..." showSearch optionFilterProp="label"
                options={urunSecenekleri} allowClear
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={handleUrunEkle}>Ekle</Button>
            </Space.Compact>

            {seciliPazar && !seciliAcik && (
              <div style={{ marginTop: 12, padding: 8, background: '#fff7e6', borderRadius: 4, fontSize: 12, color: '#d48806' }}>
                <CheckCircleOutlined /> Bu gun Pazar ve kapali olarak isaretli. Bir oge ekleyerek gunu acabilirsiniz.
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Menü Düzenleme Modal */}
      <Modal
        title={seciliGun ? `${seciliGun.format('DD MMMM YYYY')} — Günün Menüsü` : 'Günün Menüsü'}
        open={menuModalOpen}
        onCancel={() => setMenuModalOpen(false)}
        onOk={handleMenuKaydet}
        okText="Kaydet"
        cancelText="İptal"
        width={600}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Açıklama (ops.)</label>
          <Input value={menuForm.aciklama} onChange={(e) => setMenuForm({ ...menuForm, aciklama: e.target.value })} placeholder="Örn. 'Salı Menüsü'" />
        </div>
        <Divider style={{ margin: '8px 0' }}>Ürünler</Divider>
        {menuForm.kalemler.map((k, idx) => (
          <Space.Compact key={idx} style={{ display: 'flex', marginBottom: 6 }}>
            <Select
              value={k.urun_id}
              onChange={(v) => {
                const copy = [...menuForm.kalemler];
                copy[idx] = { ...copy[idx], urun_id: v };
                setMenuForm({ ...menuForm, kalemler: copy });
              }}
              placeholder="Ürün seçin..."
              showSearch optionFilterProp="label"
              onSearch={setUrunArama}
              notFoundContent={renderUrunNotFound('gunun', idx)}
              options={urunSecenekleri}
              style={{ flex: 1 }}
            />
            <InputNumber
              value={k.adet} min={1}
              onChange={(v) => {
                const copy = [...menuForm.kalemler];
                copy[idx] = { ...copy[idx], adet: v };
                setMenuForm({ ...menuForm, kalemler: copy });
              }}
              style={{ width: 80 }}
              placeholder="Adet"
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => {
              const copy = menuForm.kalemler.filter((_, i) => i !== idx);
              setMenuForm({ ...menuForm, kalemler: copy });
            }} />
          </Space.Compact>
        ))}
        <Button type="dashed" block icon={<PlusOutlined />}
          onClick={() => setMenuForm({ ...menuForm, kalemler: [...menuForm.kalemler, { urun_id: null, adet: 1 }] })}
        >
          Ürün Ekle
        </Button>
        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
          Menüde en az 3 ürün (toplam adet ≥ 3) bulunmalıdır.
        </div>
      </Modal>

      {/* Ekstra Menü Düzenleme Modal */}
      <Modal
        title={seciliGun ? `${seciliGun.format('DD MMMM YYYY')} — Ekstra Menü ${ekstraMenuEditId ? 'Düzenle' : 'Ekle'}` : 'Ekstra Menü'}
        open={ekstraMenuModalOpen}
        onCancel={() => { setEkstraMenuModalOpen(false); setEkstraMenuEditId(null); }}
        onOk={handleEkstraMenuKaydet}
        okText="Kaydet"
        cancelText="İptal"
        width={600}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Menü Adı *</label>
          <Input value={ekstraMenuForm.ad}
            onChange={(e) => setEkstraMenuForm({ ...ekstraMenuForm, ad: e.target.value })}
            placeholder="Örn. 'Çocuk Menüsü', 'VIP Menü'" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Açıklama (ops.)</label>
          <Input value={ekstraMenuForm.aciklama}
            onChange={(e) => setEkstraMenuForm({ ...ekstraMenuForm, aciklama: e.target.value })} />
        </div>
        <Divider style={{ margin: '8px 0' }}>Ürünler</Divider>
        {ekstraMenuForm.kalemler.map((k, idx) => (
          <Space.Compact key={idx} style={{ display: 'flex', marginBottom: 6 }}>
            <Select
              value={k.urun_id}
              onChange={(v) => {
                const copy = [...ekstraMenuForm.kalemler];
                copy[idx] = { ...copy[idx], urun_id: v };
                setEkstraMenuForm({ ...ekstraMenuForm, kalemler: copy });
              }}
              placeholder="Ürün seçin..."
              showSearch optionFilterProp="label"
              onSearch={setUrunArama}
              notFoundContent={renderUrunNotFound('ekstra', idx)}
              options={urunSecenekleri}
              style={{ flex: 1 }}
            />
            <InputNumber
              value={k.adet} min={1}
              onChange={(v) => {
                const copy = [...ekstraMenuForm.kalemler];
                copy[idx] = { ...copy[idx], adet: v };
                setEkstraMenuForm({ ...ekstraMenuForm, kalemler: copy });
              }}
              style={{ width: 80 }}
              placeholder="Adet"
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => {
              const copy = ekstraMenuForm.kalemler.filter((_, i) => i !== idx);
              setEkstraMenuForm({ ...ekstraMenuForm, kalemler: copy });
            }} />
          </Space.Compact>
        ))}
        <Button type="dashed" block icon={<PlusOutlined />}
          onClick={() => setEkstraMenuForm({ ...ekstraMenuForm, kalemler: [...ekstraMenuForm.kalemler, { urun_id: null, adet: 1 }] })}
        >
          Ürün Ekle
        </Button>
      </Modal>

      {/* Sabit Ürünler Modal */}
      <Modal
        title="Sabit Urunler"
        open={sabitModalOpen}
        onCancel={() => { setSabitModalOpen(false); setSabitUrunId(null); }}
        footer={null}
        width={450}
      >
        <p style={{ color: '#888', fontSize: 12, marginBottom: 12 }}>
          Sabit urunler (su, cola vb.) tum acik gunlere otomatik olarak eklenir.
        </p>
        {sabitUrunler.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {sabitUrunler.map(s => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                <Tag color="green" style={{ borderStyle: 'dashed' }}>{s.urun_adi}</Tag>
                <Popconfirm title="Kaldirilsin mi?" onConfirm={() => handleSabitUrunSil(s.id)} okText="Evet" cancelText="Hayir">
                  <Button danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
        <Space.Compact style={{ width: '100%' }}>
          <Select
            value={sabitUrunId} onChange={setSabitUrunId}
            style={{ flex: 1 }} placeholder="Urun seciniz..."
            showSearch optionFilterProp="label"
            options={urunler.filter(u => !sabitUrunIds.includes(u.id)).map(u => ({ value: u.id, label: u.urun_adi }))}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleSabitUrunEkle}>Ekle</Button>
        </Space.Compact>
      </Modal>

      {/* Excel ile Aylık Menü Yükleme Modal */}
      <Modal
        title="Excel ile Aylık Menü Yükleme"
        open={importModalOpen}
        onCancel={() => { setImportModalOpen(false); setImportPreview(null); setImportRawRows(null); setImportMevcutGunun({}); setImportMevcutEkstra({}); }}
        onOk={handleImportUygula}
        okText={importPreview ? `İçe Aktar (${importPreview.ozet.eklenecek})` : 'İçe Aktar'}
        okButtonProps={{ disabled: !importPreview || importPreview.ozet.eklenecek === 0 }}
        cancelText="İptal"
        width={900}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Format: tarih | menu_adi | urun_adi | adet"
          description={<>
            <div><strong>menu_adi</strong> boş bırakılırsa o tarih için <strong>Günün Menüsü</strong> oluşturulur. Dolu ise o adla bir <strong>Ekstra Menü</strong> tanımlanır.</div>
            <div>Aynı tarih + aynı menu_adi olan satırlar tek menünün kalemleri olarak gruplanır. Mevcut menüsü olan günler/menüler atlanır. Excel herhangi bir aya / yıla ait olabilir.</div>
          </>}
        />
        <Space style={{ marginBottom: 12 }}>
          <span>Şablon Ayı:</span>
          <DatePicker picker="month" value={importYilAy} onChange={(d) => { if (d) setImportYilAy(d); }} format="YYYY-MM" allowClear={false} />
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>Şablon İndir</Button>
          <Upload
            accept=".xlsx,.xls"
            beforeUpload={parseExcelDosyasi}
            showUploadList={false}
            maxCount={1}
          >
            <Button type="primary" icon={<UploadOutlined />} loading={importLoading}>Excel Seç</Button>
          </Upload>
        </Space>

        {importPreview && (
          <>
            <div style={{ marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <Tag color="default">Toplam satır: {importPreview.ozet.toplamSatir}</Tag>
              <Tag color="green">Eklenecek menü: {importPreview.ozet.eklenecek}</Tag>
              <Tag color="orange">Atlanacak (mevcut): {importPreview.ozet.atlanacak}</Tag>
              <Tag color="red">Hatalı satır: {importPreview.ozet.hataliSatir}</Tag>
            </div>

            {importPreview.menuler.length > 0 && (
              <Table
                size="small"
                rowKey={(_, idx) => idx}
                pagination={{ pageSize: 8, showSizeChanger: false }}
                dataSource={importPreview.menuler}
                columns={[
                  { title: 'Tarih', dataIndex: 'tarih', width: 110 },
                  { title: 'Tip', width: 110, render: (_, r) => r.isGunun
                    ? <Tag color="orange">Günün Menüsü</Tag>
                    : <Tag color="purple">Ekstra</Tag> },
                  { title: 'Menü Adı', dataIndex: 'ad', render: (v) => v || <span style={{ color: '#999' }}>—</span> },
                  { title: 'Kalem', width: 80, align: 'center', render: (_, r) => r.kalemler.length },
                  { title: 'İçerik', render: (_, r) => (
                    <span style={{ fontSize: 12, color: '#666' }}>
                      {r.kalemler.map(k => `${k.urun_adi}×${k.adet}`).join(', ')}
                    </span>
                  ) },
                  { title: 'Durum', width: 110, render: (_, r) => r.durum === 'eklenecek'
                    ? <Tag color="green">Eklenecek</Tag>
                    : <Tag color="orange">Atlanacak</Tag> },
                ]}
              />
            )}

            {importPreview.bulunamayanUrunler && importPreview.bulunamayanUrunler.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0 8px' }}>Bulunamayan Ürünler</Divider>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {importPreview.bulunamayanUrunler.map((bu, idx) => (
                    <div key={idx} style={{ padding: '8px 12px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                        <div>
                          <strong>{bu.urun_adi}</strong>
                          <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>
                            ({bu.satirSayisi} satır)
                          </span>
                          {bu.oneriler.length > 0 && (
                            <div style={{ marginTop: 4, fontSize: 12 }}>
                              <span style={{ color: '#666' }}>Benzer ürünler: </span>
                              {bu.oneriler.map(u => (
                                <Tag key={u.id} color="blue" style={{ marginRight: 4 }}>{u.urun_adi}</Tag>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button size="small" type="primary" icon={<PlusOutlined />}
                          onClick={() => handleHizliUrunAcUrunAdiyla(bu.urun_adi)}>
                          "{bu.urun_adi}" Adıyla Ekle
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {importPreview.hataliSatirlar.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0 8px' }}>Hatalı Satırlar</Divider>
                <Table
                  size="small"
                  rowKey="satir"
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  dataSource={importPreview.hataliSatirlar}
                  columns={[
                    { title: 'Satır', dataIndex: 'satir', width: 60 },
                    { title: 'Tarih', dataIndex: 'tarih', width: 110, render: v => v || <span style={{ color: '#cf1322' }}>—</span> },
                    { title: 'Menü', dataIndex: 'menu_adi', render: v => v || <span style={{ color: '#999' }}>—</span> },
                    { title: 'Ürün', dataIndex: 'urun_adi' },
                    { title: 'Adet', dataIndex: 'adet', width: 60 },
                    { title: 'Hata', render: (_, r) => r.hatalar.map((h, i) => <Tag color="red" key={i}>{h}</Tag>) },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Modal>

      {/* Hızlı Ürün Ekleme Modal */}
      <Modal
        title="Yeni Ürün Ekle"
        open={hizliUrunModalOpen}
        onCancel={() => setHizliUrunModalOpen(false)}
        onOk={handleHizliUrunKaydet}
        okText="Kaydet"
        cancelText="İptal"
        width={500}
      >
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Ürün Adı *</label>
          <Input value={hizliUrunForm.urun_adi}
            onChange={(e) => setHizliUrunForm({ ...hizliUrunForm, urun_adi: e.target.value })} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Açıklama</label>
          <Input value={hizliUrunForm.aciklama}
            onChange={(e) => setHizliUrunForm({ ...hizliUrunForm, aciklama: e.target.value })} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Tip</label>
          <Select style={{ width: '100%' }} options={urunTipiOptions}
            value={hizliUrunForm.urun_tipi_id}
            onChange={handleHizliUrunTipiDegisti}
            placeholder="Ürün tipi (ops.)" allowClear />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Birim</label>
          <Input value="porsiyon" disabled />
        </div>
        <div style={{ marginBottom: 4 }}>
          <label style={{ fontSize: 12, color: '#666' }}>Satış Fiyatı</label>
          <InputNumber {...paraInputProps} style={{ width: '100%' }}
            value={hizliUrunForm.satis_fiyati}
            onChange={(v) => setHizliUrunForm({ ...hizliUrunForm, satis_fiyati: v })} />
        </div>
        <div style={{ fontSize: 11, color: '#999' }}>
          Reçete eklemek için ileride Ürünler sayfasından düzenleyebilirsiniz.
        </div>
      </Modal>
    </div>
  );
}

export default SatisTakvimi;
